import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "@clerk/clerk-react";
import { fetchCurrentUser } from "../services/meService";
import { useTokenGetter } from "./useTokenGetter";
import type { CurrentUser } from "../types/auth.types";

/**
 * The signed-in user as our backend holds them, read once per session and kept
 * where every screen can see it.
 *
 * ## Why a provider rather than a hook per screen
 *
 * Because the point of it is that the answer is shared. The rail, the
 * dashboard's greeting and the Settings heading all name the same person, and
 * a hook that fetched per caller would ask three times for one record and then
 * let the three copies drift the moment one of them changed. Here there is one
 * copy: saving the General tab tells this provider what was stored, and the
 * rail redraws from the same state in the same commit.
 *
 * ## What it is not
 *
 * Not Clerk. Clerk owns the identity — the session, the token, the address the
 * account was opened under — and this is what the *practice* holds about the
 * same person, which is what they can actually edit. Where both know something,
 * `useSignedInUser` prefers this one and falls back to Clerk; nothing else
 * should read this context directly for a name, or the two sources start being
 * chosen between in different ways on different screens.
 */

export type CurrentUserStatus = "idle" | "loading" | "ready" | "failed";

/** What the Settings form just stored, as this context shows it at once. */
export interface SavedUserDetails {
  firstName: string;
  lastName: string;
  /** Paintable straight away — the local read of the bytes just picked, or the
   *  stored file's own URL. `""` where the picture was taken off. */
  photoUrl: string;
}

interface CurrentUserValue {
  /** The record, or `null` before the read lands and for a signed-out app. */
  user: CurrentUser | null;
  status: CurrentUserStatus;
  /** Re-reads `/api/v1/me/`. Safe to call from anywhere that changed it. */
  refresh: () => void;
  /**
   * Shows what a save stored without waiting for the read that confirms it.
   *
   * The response to the `PATCH` already carries the name and the picture — the
   * bytes of a newly picked photo are in the browser's hands, so there is
   * nothing to fetch — and a rail that waited for a round trip would keep
   * showing the old face for as long as the network took. `refresh` still runs
   * behind it, so what ends up on screen is what the backend actually kept.
   */
  showSaved: (details: SavedUserDetails) => void;
}

/**
 * What the app sees where there is no provider above it: nothing known, and
 * nothing to do about it.
 *
 * A module constant so the value is stable, and tolerant rather than throwing:
 * every signed-in screen has the provider in real use — it is mounted in `App`
 * — but a page rendered on its own in a test has none, and `useSignedInUser`
 * simply falls back to Clerk there, which is exactly what it did before this
 * context existed.
 */
const NO_CURRENT_USER: CurrentUserValue = {
  user: null,
  status: "idle",
  refresh: () => {},
  showSaved: () => {},
};

const CurrentUserContext = createContext<CurrentUserValue>(NO_CURRENT_USER);

/** The whole name, from the two halves a save reports. */
const joinName = (firstName: string, lastName: string) =>
  [firstName, lastName].filter(Boolean).join(" ").trim();

export const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Clerk's own loading state, not ours: there is no point asking our backend
  // who the caller is before there is a session to ask with, and `getToken`
  // answers `null` until there is.
  const { isLoaded: sessionLoaded, isSignedIn } = useAuth();
  const getToken = useTokenGetter();

  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<CurrentUserStatus>("idle");
  // Bumped by `refresh`, which is the whole of what re-runs the read.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionLoaded || !isSignedIn) {
      // Signed out — including the moment just after a sign-out, when the rail
      // is still on screen for a frame. Whoever was here is not here now.
      setUser(null);
      setStatus("idle");
      return;
    }

    const controller = new AbortController();
    setStatus("loading");

    fetchCurrentUser(getToken, controller.signal)
      .then((current) => {
        // Unmounted, or a refresh has already replaced this run.
        if (controller.signal.aborted) return;
        setUser(current);
        setStatus("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        // Left as whatever was last known rather than cleared: a failed refresh
        // after a save should not blank the name in the rail, and on a first
        // read there is nothing to keep anyway. `useSignedInUser` falls back to
        // what Clerk holds while this is empty, so nobody sees a blank account.
        console.error("current user failed to load:", error);
        setStatus("failed");
      });

    return () => controller.abort();
  }, [sessionLoaded, isSignedIn, getToken, attempt]);

  const refresh = useCallback(() => setAttempt((count) => count + 1), []);

  const showSaved = useCallback(
    ({ firstName, lastName, photoUrl }: SavedUserDetails) =>
      setUser((current) => ({
        // Kept from the record where there is one. On the rare path where the
        // read failed and a save landed, the id and the address are simply not
        // known — and the fields that matter here are the three being set.
        clerkUserId: current?.clerkUserId ?? "",
        email: current?.email ?? "",
        firstName,
        lastName,
        fullName: joinName(firstName, lastName),
        photoUrl,
      })),
    []
  );

  const value = useMemo(
    () => ({ user, status, refresh, showSaved }),
    [user, status, refresh, showSaved]
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

/**
 * The backend's record of the signed-in user, and the two ways to keep it true:
 * `refresh` after something changed it, `showSaved` to draw that change now.
 *
 * For displaying a name or a picture, reach for `useSignedInUser` instead — it
 * layers this over what Clerk holds, so a screen never has to decide which of
 * the two to trust.
 */
export const useCurrentUser = (): CurrentUserValue =>
  useContext(CurrentUserContext);

export default useCurrentUser;
